import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import Logo from "./Logo";

const services = [
    { to: "/comptabilite", label: "Comptabilité" },
    { to: "/fiscalite", label: "Fiscalité" },
    { to: "/tva", label: "TVA" },
    { to: "/salaires-rh", label: "Salaires et RH" },
    { to: "/administrateur-gerant", label: "Administrateur et gérant" },
    { to: "/creation-societe", label: "Création de société" },
];

export const Header = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [desktopServicesOpen, setDesktopServicesOpen] = useState(false);
    const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
    const closeTimer = useRef(null);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
        setDesktopServicesOpen(false);
        setMobileServicesOpen(false);
    }, [location.pathname]);

    const isServiceActive = services.some((s) => location.pathname === s.to);

    const openDesktopMenu = () => {
        if (closeTimer.current) {
            clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
        setDesktopServicesOpen(true);
    };

    const scheduleClose = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setDesktopServicesOpen(false), 120);
    };

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-200 ${
                scrolled
                    ? "bg-white/95 backdrop-blur-sm border-b border-[#E5E7EB]"
                    : "bg-white border-b border-transparent"
            }`}
        >
            <div className="container-prose flex items-center justify-between h-24 md:h-32">
                <Logo size="xl" />

                {/* Desktop nav */}
                <nav className="hidden md:flex items-center gap-10" aria-label="Navigation principale">
                    <NavLink
                        to="/"
                        end
                        data-testid="nav-link-accueil"
                        className={({ isActive }) =>
                            `text-sm font-medium tracking-wide transition-colors ${
                                isActive ? "text-[#1C9CAC]" : "text-[#2C3E45] hover:text-[#1C9CAC]"
                            }`
                        }
                    >
                        Accueil
                    </NavLink>

                    {/* Nos services dropdown */}
                    <div
                        className="relative"
                        onMouseEnter={openDesktopMenu}
                        onMouseLeave={scheduleClose}
                        onFocus={openDesktopMenu}
                        onBlur={scheduleClose}
                    >
                        <button
                            type="button"
                            data-testid="nav-services-trigger"
                            aria-haspopup="true"
                            aria-expanded={desktopServicesOpen}
                            onClick={() => setDesktopServicesOpen((v) => !v)}
                            className={`inline-flex items-center gap-1.5 text-sm font-medium tracking-wide transition-colors ${
                                isServiceActive
                                    ? "text-[#1C9CAC]"
                                    : "text-[#2C3E45] hover:text-[#1C9CAC]"
                            }`}
                        >
                            Nos services
                            <ChevronDown
                                size={14}
                                strokeWidth={1.75}
                                className={`transition-transform duration-200 ${
                                    desktopServicesOpen ? "rotate-180" : ""
                                }`}
                            />
                        </button>

                        {desktopServicesOpen && (
                            <div
                                data-testid="nav-services-dropdown"
                                role="menu"
                                className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-72"
                                onMouseEnter={openDesktopMenu}
                                onMouseLeave={scheduleClose}
                            >
                                <div className="bg-white border border-[#E5E7EB] shadow-lg py-2">
                                    {services.map((s) => (
                                        <NavLink
                                            key={s.to}
                                            to={s.to}
                                            role="menuitem"
                                            data-testid={`nav-services-item-${s.to.slice(1)}`}
                                            className={({ isActive }) =>
                                                `block px-5 py-3 text-sm transition-colors ${
                                                    isActive
                                                        ? "text-[#1C9CAC] bg-[#FAFAF7]"
                                                        : "text-[#2C3E45] hover:text-[#1C9CAC] hover:bg-[#FAFAF7]"
                                                }`
                                            }
                                        >
                                            {s.label}
                                        </NavLink>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <NavLink
                        to="/contact"
                        data-testid="nav-link-contact"
                        className={({ isActive }) =>
                            `text-sm font-medium tracking-wide transition-colors ${
                                isActive ? "text-[#1C9CAC]" : "text-[#2C3E45] hover:text-[#1C9CAC]"
                            }`
                        }
                    >
                        Contact
                    </NavLink>

                    <Link
                        to="/contact"
                        data-testid="header-cta-rdv"
                        className="btn-primary !py-2.5 !px-5 text-xs"
                    >
                        Prendre rendez-vous
                    </Link>
                </nav>

                {/* Mobile toggle */}
                <button
                    type="button"
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen((v) => !v)}
                    className="md:hidden inline-flex items-center justify-center w-10 h-10 text-[#2C3E45]"
                    aria-expanded={open}
                    aria-controls="mobile-nav"
                    aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {/* Mobile nav */}
            {open && (
                <div
                    id="mobile-nav"
                    data-testid="mobile-nav"
                    className="md:hidden border-t border-[#E5E7EB] bg-white"
                >
                    <nav className="container-prose flex flex-col py-6 gap-2" aria-label="Navigation mobile">
                        <NavLink
                            to="/"
                            end
                            data-testid="mobile-nav-link-accueil"
                            className={({ isActive }) =>
                                `text-base font-medium py-2 transition-colors ${
                                    isActive ? "text-[#1C9CAC]" : "text-[#2C3E45]"
                                }`
                            }
                        >
                            Accueil
                        </NavLink>

                        {/* Mobile services accordion */}
                        <button
                            type="button"
                            data-testid="mobile-services-trigger"
                            aria-expanded={mobileServicesOpen}
                            onClick={() => setMobileServicesOpen((v) => !v)}
                            className={`flex items-center justify-between py-2 text-base font-medium transition-colors ${
                                isServiceActive ? "text-[#1C9CAC]" : "text-[#2C3E45]"
                            }`}
                        >
                            <span>Nos services</span>
                            <ChevronDown
                                size={18}
                                strokeWidth={1.75}
                                className={`transition-transform duration-200 ${
                                    mobileServicesOpen ? "rotate-180" : ""
                                }`}
                            />
                        </button>

                        {mobileServicesOpen && (
                            <div
                                data-testid="mobile-services-list"
                                className="pl-4 border-l border-[#E5E7EB] ml-1 flex flex-col"
                            >
                                {services.map((s) => (
                                    <NavLink
                                        key={s.to}
                                        to={s.to}
                                        data-testid={`mobile-services-item-${s.to.slice(1)}`}
                                        className={({ isActive }) =>
                                            `py-2 text-[15px] transition-colors ${
                                                isActive
                                                    ? "text-[#1C9CAC]"
                                                    : "text-[#5C6B70] hover:text-[#1C9CAC]"
                                            }`
                                        }
                                    >
                                        {s.label}
                                    </NavLink>
                                ))}
                            </div>
                        )}

                        <NavLink
                            to="/contact"
                            data-testid="mobile-nav-link-contact"
                            className={({ isActive }) =>
                                `text-base font-medium py-2 transition-colors ${
                                    isActive ? "text-[#1C9CAC]" : "text-[#2C3E45]"
                                }`
                            }
                        >
                            Contact
                        </NavLink>

                        <Link
                            to="/contact"
                            data-testid="mobile-cta-rdv"
                            className="btn-primary mt-4 w-full"
                        >
                            Prendre rendez-vous
                        </Link>
                    </nav>
                </div>
            )}
        </header>
    );
};

export default Header;
